/**
 * Concern:
 * Shared model-id normalization for LLM usage pricing.
 * Resolves dated or aliased OpenAI model ids onto the canonical ids used by
 * the repo-managed pricing table.
 */

const OPENAI_PRICING_MODEL_ALIASES: Readonly<Record<string, string>> = {
  "gpt-5-mini": "gpt-5.4-mini",
  "gpt-5-nano": "gpt-5.4-nano",
};

const findExactOrDatedModelId = ({
  modelId,
  candidateModelIds,
}: {
  modelId: string;
  candidateModelIds: readonly string[];
}) =>
  candidateModelIds
    .slice()
    .sort((left, right) => right.length - left.length)
    .find(
      (candidateModelId) =>
        modelId === candidateModelId ||
        modelId.startsWith(`${candidateModelId}-`),
    );

export const resolveOpenAiPricingModelId = ({
  modelId,
  candidateModelIds,
}: {
  modelId: string;
  candidateModelIds: readonly string[];
}) => {
  const normalizedModelId = modelId.trim().toLowerCase();
  const resolvedExactOrDatedModelId = findExactOrDatedModelId({
    modelId: normalizedModelId,
    candidateModelIds,
  });
  if (resolvedExactOrDatedModelId) {
    return resolvedExactOrDatedModelId;
  }

  const aliasTargetModelId = Object.entries(OPENAI_PRICING_MODEL_ALIASES).find(
    ([aliasModelId]) =>
      normalizedModelId === aliasModelId ||
      normalizedModelId.startsWith(`${aliasModelId}-`),
  )?.[1];
  if (aliasTargetModelId && candidateModelIds.includes(aliasTargetModelId)) {
    return aliasTargetModelId;
  }

  return undefined;
};

export const llmUsagePricingPrivate = {
  findExactOrDatedModelId,
  OPENAI_PRICING_MODEL_ALIASES,
};
